import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen,
  Lock,
  CheckCircle2,
  ChevronDown,
  Star,
  Layers,
  Target,
  Calculator,
  Compass,
  Banknote,
  Trophy,
  GraduationCap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LessonCard } from "./LessonCard";
import { lt } from "../localize";
import type { Module, LessonProgress } from "../types";

const ICON_MAP: Record<string, React.ElementType> = {
  star: Star,
  layers: Layers,
  target: Target,
  calculator: Calculator,
  compass: Compass,
  banknote: Banknote,
  trophy: Trophy,
};

interface ModuleCardProps {
  module: Module;
  lessonProgress: Record<string, LessonProgress>;
  isLocked: boolean;
  isComplete: boolean;
  onTakeExam?: (moduleId: string) => void;
}

export const ModuleCard = ({
  module,
  lessonProgress,
  isLocked,
  isComplete,
  onTakeExam,
}: ModuleCardProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [expanded, setExpanded] = useState(false);

  const Icon = ICON_MAP[module.icon] || BookOpen;
  const completedCount = module.lessons.filter(
    (l) => lessonProgress[l.id]?.completed,
  ).length;
  const totalCount = module.lessons.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isEmpty = totalCount === 0;
  const hasLessons = totalCount > 0;

  return (
    <Card
      className={cn(
        "overflow-hidden transition-all",
        isLocked && "opacity-60",
        isComplete && "border-accent/30 bg-accent/5",
        !isLocked && !isComplete && hasLessons && "border-muted",
      )}
    >
      <button
        className="w-full p-4 flex items-start gap-3 text-left"
        onClick={() => !isLocked && hasLessons && setExpanded(!expanded)}
        disabled={isLocked || isEmpty}
      >
        <div
          className={cn(
            "rounded-lg p-2 shrink-0",
            isComplete
              ? "bg-accent"
              : isLocked
                ? "bg-muted"
                : "bg-secondary/20 border-2 border-secondary",
          )}
        >
          {isLocked ? (
            <Lock className="w-5 h-5 text-muted-foreground" />
          ) : isComplete ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : (
            <Icon className="w-5 h-5 text-secondary" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-bold text-sm">{lt(module.title, lang)}</h3>
            {isComplete && (
              <Badge className="bg-accent text-white text-[10px]">
                {t("learn_module_complete")}
              </Badge>
            )}
            {isEmpty && !isLocked && (
              <Badge variant="secondary" className="text-[10px]">
                {t("learn_coming_soon")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
            {lt(module.description, lang)}
          </p>

          {hasLessons && !isLocked && (
            <>
              <Progress value={progressPercent} className="h-1.5 mb-1" />
              <p className="text-[10px] text-muted-foreground">
                {completedCount}/{totalCount}{" "}
                {totalCount === 1 ? t("learn_lesson") : t("learn_lessons")}
              </p>
            </>
          )}

          {isLocked && (
            <p className="text-xs text-muted-foreground">
              {t("learn_locked")}
            </p>
          )}
        </div>

        {hasLessons && !isLocked && (
          <ChevronDown
            className={cn(
              "w-5 h-5 text-muted-foreground shrink-0 transition-transform",
              expanded && "rotate-180",
            )}
          />
        )}
      </button>

      {expanded && hasLessons && (
        <div className="px-3 pb-3 border-t border-muted">
          {/* Exam shortcut for experienced players */}
          {module.exam && !isComplete && onTakeExam && (
            <div className="py-3 mb-1">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-secondary/40 text-secondary hover:bg-secondary/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onTakeExam(module.id);
                }}
              >
                <GraduationCap className="mr-2 h-4 w-4" />
                {t("learn_take_exam")}
              </Button>
            </div>
          )}
          {module.lessons.map((lesson) => (
            <LessonCard
              key={lesson.id}
              lesson={lesson}
              progress={lessonProgress[lesson.id]}
            />
          ))}
        </div>
      )}
    </Card>
  );
};
